import { api } from "@/lib/api";

interface QueuedMutation {
  id: string;
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
  createdAt: number;
}

const queue: QueuedMutation[] = [];
let flushing = false;
let nextId = 0;

type QueueListener = (count: number) => void;

const listeners = new Set<QueueListener>();

export function subscribeToQueue(listener: QueueListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify() {
  for (const fn of listeners) fn(queue.length);
}

export function enqueueMutation(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): string {
  const id = `q_${nextId++}`;
  queue.push({ id, method, path, body, createdAt: Date.now() });
  notify();
  return id;
}

export function getQueueLength(): number {
  return queue.length;
}

export function getQueuedMutations(): QueuedMutation[] {
  return [...queue];
}

export async function flushQueue(): Promise<void> {
  if (flushing || queue.length === 0) return;
  flushing = true;

  const batch = [...queue];
  queue.length = 0;
  notify();

  for (const mutation of batch) {
    try {
      switch (mutation.method) {
        case "POST":
          await api.post(mutation.path, mutation.body);
          break;
        case "PUT":
          await api.put(mutation.path, mutation.body);
          break;
        case "PATCH":
          await api.patch(mutation.path, mutation.body);
          break;
        case "DELETE":
          await api.delete(mutation.path, mutation.body);
          break;
      }
    } catch {
      queue.push(mutation);
      notify();
    }
  }

  flushing = false;

  if (queue.length > 0) {
    return flushQueue();
  }
}

export function clearQueue(): void {
  queue.length = 0;
  notify();
}
